/**
 * Change Password Form Validation Schema
 *
 * Yup schema for validating password change form inputs.
 * Used with React Hook Form resolvers for client-side validation.
 * Aligns with backend password policy requirements.
 */

import * as Yup from 'yup';

/**
 * Password policy validation rules
 * Matches backend FluentValidation requirements for password strength
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one digit (0-9)
 * - At least one special character (@$!%*?&#)
 */
export const changePasswordValidationSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Aktualne hasło jest wymagane')
    .typeError('Hasło musi być tekstem'),

  newPassword: Yup.string()
    .required('Nowe hasło jest wymagane')
    .min(8, 'Hasło musi mieć minimum 8 znaków')
    .matches(/[A-Z]/, 'Hasło musi zawierać przynajmniej jedną dużą literę (A-Z)')
    .matches(/[a-z]/, 'Hasło musi zawierać przynajmniej jedną małą literę (a-z)')
    .matches(/[0-9]/, 'Hasło musi zawierać przynajmniej jedną cyfrę (0-9)')
    .matches(/[@$!%*?&#]/, 'Hasło musi zawierać przynajmniej jeden znak specjalny (@$!%*?&#)')
    .typeError('Hasło musi być tekstem'),

  confirmPassword: Yup.string()
    .required('Potwierdzenie hasła jest wymagane')
    .oneOf([Yup.ref('newPassword')], 'Hasła muszą być identyczne')
    .typeError('Hasło musi być tekstem'),
});

/**
 * Infer TypeScript type from Yup schema
 * Ensures form values match validation schema
 */
export type ChangePasswordFormInputs = Yup.InferType<typeof changePasswordValidationSchema>;
